import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Modal } from 'react-bootstrap';
import { format } from 'date-fns';
import { ZoomableImageModal } from 'Core/components';
import { ShipmentService } from '../../service/api/shipment-service';

const isCheckoutAction = action =>
  String(action || '')
    .trim()
    .toLowerCase() === 'checkout';

const toDataUrl = (fileBase64, contentType, fileName) => {
  if (!fileBase64) return '';
  const name = String(fileName || '').toLowerCase();
  let mime = contentType || 'image/jpeg';
  if (!contentType) {
    if (name.endsWith('.png')) mime = 'image/png';
    else if (name.endsWith('.gif')) mime = 'image/gif';
    else if (name.endsWith('.webp')) mime = 'image/webp';
    else if (name.endsWith('.jpg') || name.endsWith('.jpeg')) mime = 'image/jpeg';
  }
  return `data:${mime};base64,${fileBase64}`;
};

const LogTable = ({ data, asp }) => {
  const [photos, setPhotos] = useState([]);
  const [photosLoading, setPhotosLoading] = useState(false);
  const [photosError, setPhotosError] = useState('');
  const [showGallery, setShowGallery] = useState(false);
  const [showZoomModal, setShowZoomModal] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);

  const hasCheckout = useMemo(
    () => (Array.isArray(data) ? data.some(log => isCheckoutAction(log.action)) : false),
    [data],
  );

  const aspNum = String(asp || '').trim();

  useEffect(() => {
    if (!hasCheckout || !aspNum) {
      setPhotos([]);
      setPhotosError('');
      setPhotosLoading(false);
      return undefined;
    }

    let cancelled = false;
    const loadPhotos = async () => {
      setPhotosLoading(true);
      setPhotosError('');
      setPhotos([]);
      try {
        const response = await ShipmentService.getShipPhotosByAsp(aspNum);
        if (cancelled) return;
        if (response?.status === 200 && response?.response) {
          const payload = response.response;
          const list = payload.photos || payload.Photos || [];
          const mapped = (Array.isArray(list) ? list : []).map((item, index) => {
            const fileName = item.fileName || item.FileName || `photo-${index + 1}.jpg`;
            const fileBase64 = item.fileBase64 || item.FileBase64 || null;
            const contentType = item.contentType || item.ContentType || null;
            const webUrl = item.webUrl || item.WebUrl || null;
            return {
              fileName,
              webUrl,
              src: fileBase64
                ? toDataUrl(fileBase64, contentType, fileName)
                : webUrl || '',
            };
          }).filter(p => p.src);

          setPhotos(mapped);
        } else {
          setPhotos([]);
        }
      } catch (err) {
        if (cancelled) return;
        setPhotos([]);
        setPhotosError(err?.message || 'Failed to load photos');
      } finally {
        if (!cancelled) setPhotosLoading(false);
      }
    };

    loadPhotos();
    return () => {
      cancelled = true;
    };
  }, [hasCheckout, aspNum]);

  const openPhoto = useCallback(photo => {
    if (!photo?.src) return;
    setSelectedImage({ src: photo.src, name: photo.fileName });
    setShowZoomModal(true);
  }, []);

  const openGallery = useCallback(() => {
    if (photos.length === 1) {
      openPhoto(photos[0]);
      return;
    }
    setShowGallery(true);
  }, [photos, openPhoto]);

  const renderPhotoCell = log => {
    if (!isCheckoutAction(log.action)) {
      return <span className="text-muted">—</span>;
    }

    if (!aspNum) {
      return <span className="text-muted">No ASP</span>;
    }

    if (photosLoading) {
      return <span className="text-muted">Loading...</span>;
    }

    if (photosError) {
      return (
        <span className="text-danger" title={photosError}>
          Error
        </span>
      );
    }

    if (!photos.length) {
      return <span className="text-muted">No photo</span>;
    }

    return (
      <div className="d-flex align-items-center gap-2">
        <div className="d-flex align-items-center gap-1">
          {photos.slice(0, 2).map(photo => (
            <button
              key={photo.fileName}
              type="button"
              className="btn btn-link p-0 border-0"
              title={photo.fileName}
              onClick={() => openPhoto(photo)}
              style={{ lineHeight: 0 }}
            >
              <img
                src={photo.src}
                alt={photo.fileName}
                style={{
                  width: 36,
                  height: 36,
                  objectFit: 'cover',
                  borderRadius: 4,
                  border: '1px solid #dee2e6',
                }}
              />
            </button>
          ))}
        </div>
        <button
          type="button"
          className="btn btn-outline-primary btn-sm"
          title={photos.length > 1 ? `View ${photos.length} photos` : 'View photo'}
          onClick={openGallery}
        >
          <i className="feather-eye" />
          {photos.length > 1 ? (
            <span className="ms-1">{photos.length}</span>
          ) : null}
        </button>
      </div>
    );
  };

  return (
    <div className="row border">
      <div className="col-lg-12">
        <div className="card">
          <div className="card-header">
            <h5 className="card-title">User Log</h5>
          </div>
          <div className="card-body">
            <div className="table-responsive">
              <table className="table table-hover mb-0">
                <thead>
                  <tr>
                    <th>User</th>
                    <th>Action</th>
                    <th>Old Status</th>
                    <th>New Status</th>
                    <th>Date & Time</th>
                    <th>Photo</th>
                  </tr>
                </thead>
                <tbody>
                  {data && data.length > 0 ? (
                    data.map((log, index) => (
                      <tr key={index}>
                        <td>{log.user}</td>
                        <td>{log.action}</td>
                        <td>{log.oldStatus}</td>
                        <td>{log.newStatus}</td>
                        <td>
                          {log.parsedDate
                            ? format(log.parsedDate, 'PPpp')
                            : log.dateTime
                              ? format(new Date(log.dateTime), 'PPpp')
                              : ''}
                        </td>
                        <td>{renderPhotoCell(log)}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="6" className="text-center p-5">
                        No log data available.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      <Modal
        show={showGallery}
        onHide={() => setShowGallery(false)}
        size="lg"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Checkout Photos — {aspNum}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="d-flex flex-wrap gap-3">
            {photos.map(photo => (
              <button
                key={photo.fileName}
                type="button"
                className="btn btn-link p-0 border-0 text-decoration-none"
                onClick={() => {
                  setShowGallery(false);
                  openPhoto(photo);
                }}
                title={photo.fileName}
              >
                <img
                  src={photo.src}
                  alt={photo.fileName}
                  style={{
                    width: 140,
                    height: 140,
                    objectFit: 'cover',
                    borderRadius: 6,
                    border: '1px solid #dee2e6',
                  }}
                />
                <div className="small text-muted mt-1 text-truncate" style={{ maxWidth: 140 }}>
                  {photo.fileName}
                </div>
              </button>
            ))}
          </div>
        </Modal.Body>
      </Modal>

      <ZoomableImageModal
        show={showZoomModal}
        onHide={() => {
          setShowZoomModal(false);
          setSelectedImage(null);
        }}
        imageSrc={selectedImage?.src || ''}
        imageAlt={selectedImage?.name || 'Checkout photo'}
        enableEditor={false}
      />
    </div>
  );
};

LogTable.propTypes = {
  asp: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  data: PropTypes.arrayOf(
    PropTypes.shape({
      user: PropTypes.string,
      action: PropTypes.string,
      oldStatus: PropTypes.string,
      newStatus: PropTypes.string,
      dateTime: PropTypes.string,
    }),
  ),
};

LogTable.defaultProps = {
  data: [],
  asp: '',
};

export default LogTable;
